/* eslint-disable max-lines */
import { useCallback, useEffect, useRef } from "react";
import { FeatureLayer } from "@evergis/sgis/es/layers/FeatureLayer";
import { Feature } from "@evergis/sgis";
import { useMapContext } from "@evergis/react";
import { Coordinates } from "@evergis/sgis/es/baseTypes";

import { LabelFeature } from "@evergis/sgis/es/features/Label";
import { useGeometrySelection } from "./useGeometrySelection";
import { useSelectFeaturesInitialized } from "../selectFeatures/useSelectFeaturesInitialized";

import {
  GeometrySelectionToolTypes,
  GeometrySelectionTypes,
} from "../../modules/Map/components/GeometrySelectionTools/types";

import { createPointFeature } from "../../modules/Map/components/GeometrySelectionTools/Selection/utils/createPointFeature";

import {
  GeometrySelectionSymbolType,
  useSelectionSymbols,
} from "../../modules/Map/components/GeometrySelectionTools/Selection/hooks/useSelectionSymbols";

import { ShadowedPointSymbol } from "../../react-sgis/symbols/editorSymbols/ShadowedPointSymbol";
import { createLabelSymbol } from "../../react-sgis/symbols/labelSymbols/labelSymbols";
import { SelectionHistoryFeature } from "../../ducks/geometrySelection/geometrySelection";

// eslint-disable-next-line max-lines-per-function
export const useGeometrySelectionHistory = () => {
  const { map } = useMapContext();
  const layer = useRef<FeatureLayer>();
  const editLayer = useRef<FeatureLayer>();
  const bufferLayer = useRef<FeatureLayer>();
  const [geometrySelection, setGeometrySelection] = useGeometrySelection();
  const [selectFeaturesInitialized] = useSelectFeaturesInitialized();
  const symbols = useSelectionSymbols();

  const drawFeature = (value: Feature | Feature[]) => {
    layer.current?.add(value);
  };

  const drawRoutePoints = useCallback(
    (feature: SelectionHistoryFeature) => {
      drawFeature(
        createPointFeature(
          feature?._rings[0][0] as Coordinates,
          map.crs,
          symbols[GeometrySelectionSymbolType.point] as ShadowedPointSymbol,
        ),
      );

      drawFeature(
        new LabelFeature(feature?._rings[0][0] as Coordinates, {
          content: "1",
          crs: map.crs,
          symbol: createLabelSymbol("textSymbol", { offset: [0, -10] }),
        }),
      );

      drawFeature(
        createPointFeature(
          feature?._rings[0][feature?._rings[0].length - 1] as Coordinates,
          map.crs,
          symbols[GeometrySelectionSymbolType.point] as ShadowedPointSymbol,
        ),
      );

      drawFeature(
        new LabelFeature(feature?._rings[0][feature?._rings[0].length - 1] as Coordinates, {
          content: "2",
          crs: map.crs,
          symbol: createLabelSymbol("textSymbol", { offset: [0, -10] }),
        }),
      );
    },
    [map.crs, symbols],
  );

  const stepBack = useCallback(() => {
    const { currentTool } = geometrySelection;
    const historyProp = currentTool === GeometrySelectionToolTypes.Edit ? "editHistory" : "history";
    const history = geometrySelection[historyProp];
    const currentLayer = currentTool === GeometrySelectionToolTypes.Edit ? editLayer.current : layer.current;

    if (!currentLayer || !history) {
      return;
    }

    const step = history.step - 1;

    currentLayer.features =
      step === -1 && currentTool === GeometrySelectionToolTypes.Edit
        ? geometrySelection.history.features[geometrySelection.history.step]
        : history.features[step];

    if (geometrySelection.type === GeometrySelectionTypes.Route) {
      drawRoutePoints(history.features[step]?.[0]);
    }

    setGeometrySelection({
      [historyProp]: {
        ...history,
        step,
      },
    });
  }, [drawRoutePoints, geometrySelection, setGeometrySelection]);

  const stepForward = useCallback(() => {
    const { currentTool } = geometrySelection;
    const historyProp = currentTool === GeometrySelectionToolTypes.Edit ? "editHistory" : "history";
    const history = geometrySelection[historyProp];
    const currentLayer = currentTool === GeometrySelectionToolTypes.Edit ? editLayer.current : layer.current;

    if (!currentLayer || !history || history.step === history.features.length - 1) {
      return;
    }

    const step = history.step + 1;

    currentLayer.features = history.features[step];

    if (geometrySelection.type === GeometrySelectionTypes.Route) {
      drawRoutePoints(history.features[step]?.[0]);
    }

    setGeometrySelection({
      [historyProp]: {
        ...history,
        step,
      },
    });
  }, [drawRoutePoints, geometrySelection, setGeometrySelection]);

  const addFeature = useCallback(
    (value: Feature | Feature[], rewrite?: boolean) => {
      const { currentTool } = geometrySelection;
      const historyProp = currentTool === GeometrySelectionToolTypes.Edit ? "editHistory" : "history";
      const history = geometrySelection[historyProp];
      const currentLayer = currentTool === GeometrySelectionToolTypes.Edit ? editLayer.current : layer.current;

      if (!currentLayer || !history) {
        return;
      }

      let processedFeatures = [...history.features];

      if (history.step < history.features.length - 1) {
        processedFeatures = processedFeatures.filter((_, index) => index <= history.step);
      }

      if (history.step > -1) {
        processedFeatures[history.step].forEach(feature => {
          currentLayer.has(feature) && currentLayer.remove(feature);
        });
      }

      const newFeatures = rewrite
        ? Array.isArray(value)
          ? [...processedFeatures, [...value]]
          : [...processedFeatures, [value]]
        : Array.isArray(value)
        ? [...processedFeatures, [...(processedFeatures[processedFeatures.length - 1] || []), ...value]]
        : [...processedFeatures, [...(processedFeatures[processedFeatures.length - 1] || []), value]];

      const newStep = history.step + 1;

      currentLayer.add(newFeatures[newStep]);

      setGeometrySelection({
        [historyProp]: {
          features: newFeatures,
          step: newStep,
        },
      });
    },
    [geometrySelection, setGeometrySelection],
  );

  const replaceFeature = useCallback(
    (value: Feature | Feature[]) => {
      const { currentTool } = geometrySelection;
      const historyProp = currentTool === GeometrySelectionToolTypes.Edit ? "editHistory" : "history";
      const history = geometrySelection[historyProp];
      const currentLayer = currentTool === GeometrySelectionToolTypes.Edit ? editLayer.current : layer.current;

      if (!currentLayer || !history) {
        return;
      }

      let processedFeatures = [...history.features];

      if (history.step > -1) {
        processedFeatures = processedFeatures.filter((_, index) => index <= history.step);

        processedFeatures[history.step].forEach(feature => {
          currentLayer.has(feature) && currentLayer.remove(feature);
        });
      }

      const newFeatures = Array.isArray(value) ? [...processedFeatures, [...value]] : [...processedFeatures, [value]];
      const newStep = history.step + 1;

      currentLayer.add(newFeatures[newStep]);

      setGeometrySelection({
        [historyProp]: {
          features: newFeatures,
          step: newStep,
        },
      });
    },
    [geometrySelection, setGeometrySelection],
  );

  const setFeatures = useCallback(
    (value: Feature | Feature[]) => {
      const { currentTool } = geometrySelection;
      const historyProp = currentTool === GeometrySelectionToolTypes.Edit ? "editHistory" : "history";
      const currentLayer = currentTool === GeometrySelectionToolTypes.Edit ? editLayer.current : layer.current;

      if (!layer || !geometrySelection[historyProp]) {
        return;
      }

      const newFeatures = [...geometrySelection[historyProp].features];

      newFeatures.push(Array.isArray(value) ? [...value] : [value]);
      const newStep = newFeatures.length - 1;

      currentLayer.features = newFeatures[newStep];

      setGeometrySelection({
        [historyProp]: {
          features: newFeatures,
          step: newStep,
        },
      });
    },
    [geometrySelection, setGeometrySelection],
  );

  const purgeFeatures = useCallback(() => {
    if (layer.current) {
      layer.current.features = [];
    }
  }, []);

  const saveEditState = useCallback(() => {
    const { history, editHistory } = geometrySelection;

    if (editHistory.step < 0) {
      return;
    }

    layer.current.features = editHistory.features[editHistory.step];

    setGeometrySelection({
      history: {
        features: [...history.features, editHistory.features[editHistory.step]],
        step: history.step + 1,
      },
      editHistory: {
        features: [],
        step: -1,
      },
    });
  }, [geometrySelection, setGeometrySelection]);

  useEffect(() => {
    if (selectFeaturesInitialized && !layer.current && !bufferLayer.current && !editLayer.current) {
      layer.current = new FeatureLayer();
      bufferLayer.current = new FeatureLayer();
      editLayer.current = new FeatureLayer();

      map.addLayer(bufferLayer.current);
      map.addLayer(layer.current);
      map.addLayer(editLayer.current);
    }
  }, [selectFeaturesInitialized]); // eslint-disable-line

  useEffect(() => {
    return () => {
      if (layer.current && bufferLayer.current && editLayer.current) {
        map.removeLayer(bufferLayer.current);
        map.removeLayer(layer.current);
        map.removeLayer(editLayer.current);

        layer.current = undefined;
        bufferLayer.current = undefined;
        editLayer.current = undefined;
      }
    };
  }, []);

  return {
    drawFeature,
    addFeature,
    replaceFeature,
    setFeatures,
    stepBack,
    stepForward,
    purgeFeatures,
    saveEditState,
    layer: layer.current,
    bufferLayer: bufferLayer.current,
    editLayer: editLayer.current,
  };
};
